import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { createFormatters, type Formatters } from './format';

interface FormatContextValue {
  timeZone: string;
  locale?: string | undefined;
}

const FormatContext = createContext<FormatContextValue>({ timeZone: 'UTC' });

export interface FormatProviderProps extends FormatContextValue {
  children: ReactNode;
}

/**
 * Supplies the timezone (normally the current store's) and optionally a locale override.
 * Locale defaults to the active i18next language.
 */
export function FormatProvider({ timeZone, locale, children }: FormatProviderProps) {
  const value = useMemo(() => ({ timeZone, locale }), [timeZone, locale]);
  return <FormatContext.Provider value={value}>{children}</FormatContext.Provider>;
}

/** Memoised formatters for the current locale and store timezone. */
export function useFormat(): Formatters {
  const { i18n } = useTranslation();
  const { timeZone, locale } = useContext(FormatContext);
  const resolvedLocale = locale ?? i18n.language;
  return useMemo(
    () => createFormatters({ locale: resolvedLocale, timeZone }),
    [resolvedLocale, timeZone],
  );
}

export { useTranslation, Trans } from 'react-i18next';
